import React, { useMemo } from 'react'
import { formatCompactINR } from '../../utils/currencyFormat'
import { buildEmptyFySeries } from '../../utils/financialYearMonths'
import './FyMonthlyBarChart.css'

/**
 * Financial-year bar chart. Months run from the FY start through the current month.
 * A month with no billing stays as a short mark so the axis does not collapse.
 */
export default function FyMonthlyBarChart({
  series,
  valueKey = 'revenue',
  formatValue = formatCompactINR,
  formatTooltip,
  fiscalYearStartMonth = 4,
  emptyMessage = 'No data for this financial year',
}) {
  const months = useMemo(() => {
    if (Array.isArray(series) && series.length > 0) return series
    return buildEmptyFySeries(valueKey, fiscalYearStartMonth)
  }, [series, valueKey, fiscalYearStartMonth])

  const max = useMemo(
    () => Math.max(...months.map((month) => Number(month[valueKey]) || 0), 0),
    [months, valueKey]
  )
  const hasData = max > 0

  if (!hasData && emptyMessage) {
    return <div className="fy-empty">{emptyMessage}</div>
  }

  return (
    <div className="fy-chart">
      <div className="fy-plot" role="img" aria-label="Billed by month">
        {months.map((item, index) => {
          const value = Number(item[valueKey]) || 0
          const height = max > 0 ? Math.max((value / max) * 100, value > 0 ? 8 : 0) : 0
          const tip = formatTooltip
            ? formatTooltip(value, item)
            : (formatValue(value) || String(value))
          return (
            <div key={item.key || `${item.month}-${index}`} className="fy-col" title={tip}>
              <span className="fy-value">{value > 0 ? formatValue(value) : ''}</span>
              <div className="fy-track">
                <div
                  className={`fy-bar${value <= 0 ? ' is-zero' : ''}${value > 0 && value === max ? ' is-peak' : ''}`}
                  style={{ height: value > 0 ? `${height}%` : undefined }}
                />
              </div>
              <span className="fy-month">{item.month}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
